import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Asset URLs
const LOGO_URL = "https://customer-assets.emergentagent.com/job_nordic-cruise-launch/artifacts/ae74l9ew_DUNDER%20LOGO%20SMOKE.png";

export default function AmbassadorPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    age: "",
    instagram: "",
    tiktok: "",
    about: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.city || !formData.age || !formData.about) {
      toast.error("Fyll i alla obligatoriska fält");
      return;
    }

    if (parseInt(formData.age) < 18) {
      toast.error("Du måste vara minst 18 år");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/ambassador`, {
        ...formData,
        age: parseInt(formData.age)
      });
      setIsSuccess(true);
      toast.success("Din ansökan har skickats!");
    } catch (error) {
      const message = error.response?.data?.detail || "Något gick fel. Försök igen.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] relative overflow-hidden" role="main">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gold-radial pointer-events-none" aria-hidden="true" />
      
      {/* Main content */}
      <article className="relative z-10 min-h-screen flex flex-col items-center px-4 py-8 md:py-16">
        
        {/* Back link */}
        <Link 
          to="/" 
          className="self-start mb-6 flex items-center gap-2 text-gray-400 hover:text-[#FFD700] transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Tillbaka
        </Link>

        {/* Logo */}
        <header className="w-full max-w-[200px] md:max-w-[280px] mb-6 md:mb-8">
          <img 
            src={LOGO_URL} 
            alt="Dunderkryssningen"
            className="w-full drop-shadow-[0_0_15px_rgba(255,215,0,0.15)]"
            data-testid="ambassador-logo"
            loading="eager"
          />
        </header>

        {/* Hero Text */}
        <section className="text-center mb-6 md:mb-8" aria-labelledby="ambassador-headline">
          <h1 
            id="ambassador-headline"
            className="font-heading text-3xl sm:text-4xl md:text-5xl text-white uppercase tracking-tight leading-none mb-3"
            data-testid="ambassador-headline"
          >
            Bli Ambassadör
          </h1>
          <p className="text-gray-400 text-sm md:text-base max-w-md mx-auto">
            Hjälp oss sprida Dunderkryssningen och få chansen att vara med och forma eventet
          </p>
        </section>

        {/* Form Container */}
        <section className="w-full max-w-lg" aria-label="Ambassadör-ansökan">
          <div 
            className={`bg-[#0A0A0A] border border-[#222] p-6 md:p-8 ${isSuccess ? 'animate-success' : ''}`}
            data-testid="ambassador-form-container"
          >
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                    Namn *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Ditt fullständiga namn"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                    data-testid="input-name"
                  />
                </div>

                {/* Email & Phone row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      E-post *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="din@email.se"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      Telefon *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="070-123 45 67"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* City & Age row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      Stad *
                    </label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="Stockholm"
                      value={formData.city}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <label htmlFor="age" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      Ålder *
                    </label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      min="18"
                      placeholder="25"
                      value={formData.age}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-age"
                    />
                  </div>
                </div>

                {/* Social Media row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="instagram" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      Instagram
                    </label>
                    <Input
                      id="instagram"
                      name="instagram"
                      type="text"
                      placeholder="@dittanvändarnamn"
                      value={formData.instagram}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-instagram"
                    />
                  </div>
                  <div>
                    <label htmlFor="tiktok" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                      TikTok
                    </label>
                    <Input
                      id="tiktok"
                      name="tiktok"
                      type="text"
                      placeholder="@dittanvändarnamn"
                      value={formData.tiktok}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-12 rounded-sm"
                      data-testid="input-tiktok"
                    />
                  </div>
                </div>

                {/* About */}
                <div>
                  <label htmlFor="about" className="text-xs font-bold uppercase tracking-widest text-[#FFD700] mb-2 block text-left">
                    Berätta om dig själv *
                  </label>
                  <Textarea
                    id="about"
                    name="about"
                    placeholder="Varför vill du bli ambassadör? Vad kan du bidra med? Berätta lite om dig själv..."
                    value={formData.about}
                    onChange={handleChange}
                    rows={4}
                    className="bg-[#1A1A1A] border-none text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] rounded-sm resize-none"
                    data-testid="input-about"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FFD700] text-black font-bold uppercase tracking-widest hover:bg-[#E5C100] hover:scale-[1.02] transition-all duration-300 h-14 rounded-sm gold-glow gold-glow-hover text-base mt-2"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Skickar...
                    </>
                  ) : (
                    "Skicka ansökan"
                  )}
                </Button>
              </form>
            ) : (
              /* Success State */
              <div className="text-center py-8" data-testid="success-message">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FFD700]/20 mb-4">
                  <CheckCircle className="w-8 h-8 text-[#FFD700]" aria-hidden="true" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-white uppercase mb-3">
                  Tack för din ansökan!
                </h2>
                <p className="text-gray-400 text-base mb-6">
                  Vi går igenom alla ansökningar och hör av oss om du blir utvald.
                </p>
                <Link 
                  to="/"
                  className="inline-block text-[#FFD700] hover:underline"
                >
                  Tillbaka till startsidan
                </Link>
              </div>
            )}
          </div>
        </section>
      </article>
    </main>
  );
}
